// Vendors
import React, { useCallback } from 'react'
import { Controller, FieldError, useForm } from 'react-hook-form'

// Hooks
import { useError } from 'hooks/useError'

// Components
import { FieldSelect } from 'components/FieldSelect'
import { FieldTextArea } from 'components/FieldTextArea'

// Styles
import {
  Stack,
  Modal,
  Button,
  ModalBody,
  ModalFooter,
  ButtonGroup,
  ModalHeader,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
} from '@chakra-ui/react'
import { apiPatch } from 'services/patch'
import { useSelectParser } from 'hooks/useSelectParser'

const SHIPPING_METHOD = ['Correios SEDEX', 'Motoboy']

type ModalAlterShippingMethodProps = {
  isOpen: boolean
  reload: () => void
  onCancel: () => void
  purchaseId: string
}

type FormAlterKitStatus = {
  shippingMethod: {
    label: string
    value: string
  }
  comments: string
}

export const ModalAlterShippingMethod = (
  props: ModalAlterShippingMethodProps
) => {
  const selectShippingMethod = useSelectParser({
    accessor: { label: 'providerName', value: 'id' },
    endpoint: '/public/purchase/shipping_method',
  })

  const optionsShippingMethod = selectShippingMethod.options.filter((option) =>
    SHIPPING_METHOD.includes(option.label)
  )

  const { isOpen, reload, onCancel, purchaseId } = props

  const {
    reset,
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormAlterKitStatus>()

  const { handleError } = useError()

  const onClose = useCallback(() => {
    reset()
    onCancel()
  }, [onCancel, reset])

  const onSubmit = useCallback(
    async (data: FormAlterKitStatus) => {
      try {
        const {
          comments,
          shippingMethod: { value: shippingMethodValue },
        } = data

        await apiPatch(`/app/purchase/${purchaseId}`, {
          shippingMethodId: shippingMethodValue,
          comments,
        })

        reload()
        onClose()
      } catch (error) {
        handleError(error)
      }
    },
    [handleError, onClose, purchaseId, reload]
  )

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />

      <ModalContent as="form" onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader>Alterar o método de envio</ModalHeader>

        <ModalCloseButton />

        <ModalBody>
          <Stack>
            <Controller
              control={control}
              name="shippingMethod"
              rules={{
                required: 'Por favor, selecione um método de envio',
              }}
              render={({ field }) => (
                <FieldSelect
                  label="Escolha um método de envio"
                  error={errors.shippingMethod as FieldError}
                  options={optionsShippingMethod}
                  placeholder="Selecione um método de envio..."
                  {...field}
                />
              )}
            />

            <FieldTextArea
              label="Justificativa"
              error={errors.comments}
              placeholder="Digite uma justificativa..."
              {...register('comments')}
            />
          </Stack>
        </ModalBody>

        <ModalFooter>
          <ButtonGroup>
            <Button onClick={onClose}>Cancelar</Button>
            <Button type="submit" colorScheme="blue" isLoading={isSubmitting}>
              Confirmar
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
